import React, { useEffect, useRef } from "react";
import { useParams, Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  LinearProgress,
  Button,
} from "@mui/material";
import { ArrowBack, Refresh } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  fetchOrganizationDetails,
  fetchOrganizationEventsTruncated,
  fetchOrganizationOrdersTruncated,
  fetchOrganizationSquareLocations,
  fetchOrganizationSquareTokens,
  crmSliceSelector,
} from "../../../redux/features/crmSlice";

export default function CRMOrganizationLayout() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const crmState = useSelector((state) => crmSliceSelector(state));
  const {
    organizationDetails,
    organizationDetailsLoading,
    organizationEventsTruncatedLoading,
    organizationOrdersTruncatedLoading,
    organizationSquareLocationsLoading,
    organizationSquareTokensLoading,
  } = crmState;

  const organization = organizationDetails[organizationId];
  const isLoading = organizationDetailsLoading[organizationId];

  const handleRefresh = () => {
    // Force refetch all data
    dispatch(fetchOrganizationDetails({ organizationId }));
    dispatch(fetchOrganizationEventsTruncated({ organizationId }));
    dispatch(fetchOrganizationOrdersTruncated({ organizationId }));
    dispatch(fetchOrganizationSquareLocations({ organizationId }));
    dispatch(fetchOrganizationSquareTokens({ organizationId }));
  };

  const isAnyLoading =
    isLoading ||
    organizationEventsTruncatedLoading[organizationId] ||
    organizationOrdersTruncatedLoading[organizationId] ||
    organizationSquareLocationsLoading[organizationId] ||
    organizationSquareTokensLoading[organizationId];

  // Track what we've attempted to fetch to prevent infinite loops
  const fetchAttemptedRef = useRef(false);

  useEffect(() => {
    if (!organizationId) return;

    // Reset fetch attempts when organizationId changes
    fetchAttemptedRef.current = false;
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;

    // Only fetch if not already loading, data doesn't exist, and we haven't attempted yet
    if (!organization && !isLoading && !fetchAttemptedRef.current) {
      fetchAttemptedRef.current = true;
      dispatch(fetchOrganizationDetails({ organizationId }));
    }
  }, [organizationId, organization, isLoading, dispatch]);

  return (
    <Container>
      <Box marginTop={2} marginBottom={2} display="flex" gap={2}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/crm/organizations")}
        >
          Back to Organizations
        </Button>
        <Button
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={isAnyLoading}
        >
          Refresh
        </Button>
      </Box>

      {isLoading && <LinearProgress sx={{ marginTop: 2, marginBottom: 2 }} />}

      {organization && (
        <>
          <Typography variant="h2" paddingTop={"20px"}>
            {organization.name || "Unnamed Organization"}
          </Typography>

          {/* Render child routes */}
          <Outlet />
        </>
      )}
    </Container>
  );
}
